import { Workbook, Worksheet } from 'exceljs';
import { GradeFactorDocumentType, TicketGroupBuilder } from './TicketGroupBuilder';
import { safeParseDecimal } from "@/lib/api/common";
import { createGradeFactorWorkbook } from './workbookFormatter';

export interface MonthlyReports {
  apUS?: Workbook;
  apCAD?: Workbook;
  arCAD?: Workbook;
  arUS?: Workbook;
}

export interface GradeFactorReport {
  currentMonth: MonthlyReports;
  previousMonth: MonthlyReports;
  gfCharges: Workbook | Workbook[];
}

export async function processGradeFactorReport(reports: GradeFactorReport, currentMonthName: string, previousMonthName: string, CADtoUSD_rate?: number): Promise<Workbook> {
  try {
    console.log('Processing Grade Factor Reports...');
    const ticketBuilder = new TicketGroupBuilder();
    const gfChargesWorkbooks = Array.isArray(reports.gfCharges) ? reports.gfCharges : [reports.gfCharges];

    // Process APAR workbooks
    const aparWorkbooks = [
      { workbook: reports.previousMonth.apUS, type: 'apUS' as GradeFactorDocumentType, version: 'previousMonth' as const },
      { workbook: reports.previousMonth.apCAD, type: 'apCAD' as GradeFactorDocumentType, version: 'previousMonth' as const },
      { workbook: reports.previousMonth.arCAD, type: 'arCAD' as GradeFactorDocumentType, version: 'previousMonth' as const },
      { workbook: reports.previousMonth.arUS, type: 'arUS' as GradeFactorDocumentType, version: 'previousMonth' as const },
      { workbook: reports.currentMonth.apUS, type: 'apUS' as GradeFactorDocumentType, version: 'currentMonth' as const },
      { workbook: reports.currentMonth.apCAD, type: 'apCAD' as GradeFactorDocumentType, version: 'currentMonth' as const },
      { workbook: reports.currentMonth.arCAD, type: 'arCAD' as GradeFactorDocumentType, version: 'currentMonth' as const },
      { workbook: reports.currentMonth.arUS, type: 'arUS' as GradeFactorDocumentType, version: 'currentMonth' as const }
    ].filter(entry => entry.workbook !== undefined);

    // Process each APAR workbook
    for (const { workbook, type, version } of aparWorkbooks) {
      if (!workbook) continue;

      const worksheetResults = getRelevantWorksheet_APAR(workbook);

      for (const { worksheet, columnLetters } of worksheetResults) {
        // Skip the header row
        for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
          const row = worksheet.getRow(rowNumber);

          // Get required values from specific columns
          const ticketNumber = row.getCell(columnLetters.ticketNumber).value?.toString();
          const commodityId = row.getCell(columnLetters.commodityId).value?.toString();
          const discount = safeParseDecimal({})(row.getCell(columnLetters.discount).value as any || "0").toNumber();
          const fullName = row.getCell(columnLetters.fullName).value?.toString();
          const purchaseOrSales = type.startsWith('ap') ? 'P' : 'S';

          // Skip rows without required data
          if (!ticketNumber || !commodityId) continue;


          const currency = /(cad|can)/i.test(type || "") ? "CAD$" : "USD$";
          const isCanadian = /(cad|can)$/i.test(currency || "");

          if (CADtoUSD_rate == null && isCanadian) {
            console.warn(`Workbook (${type}, ${version}) row #${rowNumber} is specified in CAD$, but no conversion rate is specified`)
          }
          const conversionFactor = !isCanadian ? 1 : (CADtoUSD_rate ?? 1);

          const shouldNegateValue =
            version == "previousMonth"
              ? purchaseOrSales == "P"
              : purchaseOrSales == "S";

          const reportedDiscount = safeParseDecimal({})(discount || 0).times(shouldNegateValue ? -1 : 1).times(conversionFactor).toNumber();

          ticketBuilder.push(commodityId, ticketNumber, {
            document: type,
            documentVersion: version,
            purchaseOrSales,
            fullName: fullName || '',
            discount: discount || 0,
            // For the previous month, report the opposite value for discount
            reportedDiscount,
          });
        }
      }
    }

    // Process GF Charges workbook
    const gfWorksheets = gfChargesWorkbooks.flatMap(gfCharges => getRelevantWorksheet_GFCharges(gfCharges));
    for (const worksheet of gfWorksheets) {
      // Skip the header row
      for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
        const row = worksheet.getRow(rowNumber);

        // Get required values from specific columns
        const ticketNumber = row.getCell('H').value?.toString();
        const commodityId = row.getCell('A').value?.toString();
        const currency = row.getCell('C').value?.toString().trim();
        const rawTotal = safeParseDecimal({})(row.getCell('AR').value as any || "0").toNumber() || 0;
        const fullName = row.getCell('E').value?.toString();

        const isCanadian = /(cad|can)$/i.test(currency || "");
        if (CADtoUSD_rate == null && isCanadian) {
          console.warn(`GF row #${rowNumber} is specified in CAD$, but no conversion rate is specified`)
        }

        const usdTotal = !isCanadian ? rawTotal : safeParseDecimal({})(rawTotal).times(CADtoUSD_rate ?? 1).toNumber();

        // Skip rows without required data
        if (!ticketNumber || !commodityId) continue;

        ticketBuilder.push(commodityId, ticketNumber, {
          document: 'gfCharges',
          documentVersion: 'currentMonth',
          purchaseOrSales: 'S',
          fullName: fullName || '',
          discount: usdTotal,
          reportedDiscount: usdTotal,
          _preConversionDiscount: rawTotal,
        });
      }
    }

    // Create and format the workbook using the new formatter
    return createGradeFactorWorkbook(ticketBuilder, currentMonthName, previousMonthName);
  } catch (error) {
    console.error('Error processing grade factor reports:', error);
    throw error;
  }
}

export interface WorksheetResult {
  worksheet: Worksheet,
  columnLetters: {
    ticketNumber: string,
    commodityId: string,
    discount: string,
    fullName: string,
  },
}

export function testWorksheetColumns_APAR(worksheet: Worksheet, useAltColumns: boolean) {
  const columnLetters = {
    ticketNumber: useAltColumns ? 'R' : 'O',
    commodityId: 'E',
    discount: useAltColumns ? 'Z' : 'W',
    fullName: 'J',
  };

  // Get the first row which should contain headers
  const headerRow = worksheet.getRow(1);

  // Check specific columns using their letter names
  const ticketNumberValue = headerRow.getCell(columnLetters.ticketNumber).value?.toString().toLowerCase().trim() || '';
  const commodityIdValue = headerRow.getCell(columnLetters.commodityId).value?.toString().toLowerCase().trim() || '';
  const discountValue = headerRow.getCell(columnLetters.discount).value?.toString().toLowerCase().trim() || '';

  const hasTicketNumber = ticketNumberValue === 'ticket number' || ticketNumberValue === 'ticket no';
  const hasCommodityId = commodityIdValue === 'commodity id';
  const hasDiscount = discountValue === 'discount';

  return {
    hasCommodityId,
    hasDiscount,
    hasTicketNumber,
    ticketNumberValue,
    commodityIdValue,
    discountValue,

    isValid: hasTicketNumber && hasCommodityId && hasDiscount,
    columnLetters,
    useAltColumns,
  }
}

export function getRelevantWorksheet_APAR(workbook: Workbook): WorksheetResult[] {
  const matchingWorksheets: WorksheetResult[] = [];

  workbook.worksheets.forEach(worksheet => {
    const std = testWorksheetColumns_APAR(worksheet, false);
    const alt = testWorksheetColumns_APAR(worksheet, true);

    if (!(std.isValid || alt.isValid)) {
      console.warn(`Warning: Worksheet "${worksheet.name}" does not match the expected format. Missing required columns.`, { std, alt });
    }
    else {
      const { columnLetters } = std.isValid ? std : alt;
      matchingWorksheets.push({
        worksheet,
        columnLetters,
      });
    }
  });

  if (matchingWorksheets.length !== 1) {
    console.warn(`Warning: Found ${matchingWorksheets.length} worksheets matching the required format. Expected 1.`);
  }

  return matchingWorksheets;
}

export function getRelevantWorksheet_GFCharges(workbook: Workbook): Worksheet[] {
  const matchingWorksheets: Worksheet[] = [];

  workbook.worksheets.forEach(worksheet => {
    // Get the first row which should contain headers
    const headerRow = worksheet.getRow(1);

    // Check specific columns using their letter names
    const ticketNumberValue = headerRow.getCell('H').value?.toString().toLowerCase().trim() || '';
    const commodityValue = headerRow.getCell('A').value?.toString().toLowerCase().trim() || '';
    const currencyValue = headerRow.getCell('C').value?.toString().toLowerCase().trim() || '';
    const totalValue = headerRow.getCell('AR').value?.toString().toLowerCase().trim() || '';

    const hasTicketNumber = ticketNumberValue === 'ticket number' || ticketNumberValue === 'ticket no';
    const hasCommodity = commodityValue === 'commodity id';
    const hasCurrency = currencyValue === 'currency';
    const hasTotal = totalValue === 'total';

    if (!hasTicketNumber || !hasCommodity || !hasCurrency || !hasTotal) {
      console.warn(`Warning: Worksheet "${worksheet.name}" does not match the expected GF Charges format. Missing required columns.`, { hasCommodity, hasCurrency, hasTotal, hasTicketNumber, ticketNumberValue, commodityValue, currencyValue, totalValue });
    } else {
      matchingWorksheets.push(worksheet);
    }
  });

  if (matchingWorksheets.length !== 1) {
    console.warn(`Warning: Found ${matchingWorksheets.length} GF Charges worksheets matching the required format. Expected 1.`);
  }

  return matchingWorksheets;
}
